import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import BlockOrderingBuilderNodeView from './block-ordering-builder-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        blockOrderingBuilder: {
            insertBlockOrderingBuilder: (options?: InsertBlockOrderingBuilderOptions) => ReturnType;
        };
    }
}

export interface InsertBlockOrderingBuilderOptions {
    name?: string;
    id?: string;
    caption?: string;
    blockOrderingXMLToLoad?: string;
    options?: { [key: string]: any };
    challenge?: boolean;
}

export const BlockOrderingBuilderNodeHeadless = Node.create({
    name: 'blockOrderingBuilder',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            name: {
                default: '',
                parseHTML: element => element.getAttribute('data-tool-name') || '',
                renderHTML: attributes => ({
                    'data-tool-name': attributes.name,
                }),
            },
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-block-ordering-builder-id'),
                renderHTML: attributes => ({
                    'data-block-ordering-builder-id': attributes.id,
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
            blockOrderingXMLToLoad: {
                default: '',
                parseHTML: element => element.getAttribute('data-block-ordering-xml') || '',
                renderHTML: attributes => ({
                    'data-block-ordering-xml': attributes.blockOrderingXMLToLoad,
                }),
            },
            options: {
                default: {},
                parseHTML: element => {
                    const raw = element.getAttribute('data-options');
                    try {
                        return raw ? JSON.parse(raw) : {};
                    } catch (error) {
                        console.error('Failed to parse data-options:', error, raw);
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-options': JSON.stringify(attributes.options),
                }),
            },
            challenge: {
                default: false,
                parseHTML: element => element.getAttribute('data-challenge') === 'true',
                renderHTML: attributes => ({
                    'data-challenge': attributes.challenge ? 'true' : 'false',
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="block-ordering-builder"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'block-ordering-builder',
                class: 'block-ordering-builder-container',
            }),
        ];
    },

    addCommands() {
        return {
            insertBlockOrderingBuilder:
                (options: InsertBlockOrderingBuilderOptions = {}) =>
                ({ commands }) => {
                    const emptyBlockOrderingXML = `<zyTool name="BlockOrderingPA" caption="" parts="1" challenge="false" edited-last="Wed Jan 28 2026 09:39:10 GMT-0800 (Pacific Standard Time)" image-ids=",,"><zyOptions type="dict"><blocks type="list"><li type="dict"><content type="string"></content><hint type="string"></hint><dependsOn type="list"></dependsOn>
<explanation type="string"></explanation>
</li></blocks><orderedListName type="string">proof</orderedListName><maxActivityAreaHeight type="number">350</maxActivityAreaHeight><prompt type="string"></prompt></zyOptions></zyTool>`;

                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            caption: options.caption || '',
                            blockOrderingXMLToLoad: options.blockOrderingXMLToLoad || emptyBlockOrderingXML,
                            name: options.name || 'BlockOrderingPA',
                            options: options.options || {},
                            challenge: options.challenge || false,
                        },
                    });
                },
        };
    },
});

export const BlockOrderingBuilderNode = BlockOrderingBuilderNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(BlockOrderingBuilderNodeView);
    },
});

export default BlockOrderingBuilderNode;
